
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import DatePicker from '@/components/DatePicker.vue';
import Modal from '@/components/Modal.vue';
import { deepCopy } from '@/utilities/cloning';
import { CovariateValue } from '@/service/models/request/computation/covariate-value';
import { DrugCovariate } from '@/service/models/response/drug-model-detail/covariate/drug-covariate';
import { useRequestDataStore } from '@/store/request-data';
import CovariateSexField from './covariate-fields/CovariateSexField.vue';
import CovariateStandardDoubleField from './covariate-fields/CovariateStandardDoubleField.vue';
import CovariateStandardBoolField from './covariate-fields/CovariateStandardBoolField.vue';
import CovariateStandardDateField from './covariate-fields/CovariateStandardDateField.vue';
import CovariateStandardIntField from './covariate-fields/CovariateStandardIntField.vue';

export default defineComponent({
    components: {
        DatePicker,
        Modal,
        CovariateSexField,
        CovariateStandardDoubleField,
        CovariateStandardBoolField,
        CovariateStandardDateField,
        CovariateStandardIntField,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },

        covariate: {
            type: null as unknown as PropType<DrugCovariate>,
            required: true,
        },

        data: {
            type: null as unknown as PropType<CovariateValue>,
            required: true,
        },
    },

    emits: {
        "update:isOpen": (payload: boolean) => typeof payload === "boolean",
        "save": (payload: CovariateValue) => typeof payload === "object",
    },

    setup(props, {emit})
    {
        const requestData = useRequestDataStore();

        // Needs to perform a deep copy of the object because we don't want the passed object
        // to be modified until the user clicks the "save" button.
        const internalData = ref(deepCopy(props.data));

        // The internal data object needs to be copied either when the parent component changes it
        // or each time the modal is re-opened (for example to create multiple new items).
        watch(
            [() => props.data, () => props.isOpen],
            () => internalData.value = deepCopy(props.data)
        );

        // Computed properties to choose which field to display depending on the covariate type
        const fieldIsSex = computed(() => props.covariate.type === DrugCovariate.Type.SEX);
        const filedIsAge = computed(() => {
            return [
                DrugCovariate.Type.AGE_IN_YEARS,
                DrugCovariate.Type.AGE_IN_MONTHS,
                DrugCovariate.Type.AGE_IN_WEEKS,
                DrugCovariate.Type.AGE_IN_DAYS,
            ].includes(props.covariate.type);
        });
        const fieldIsStandardDouble = computed(() => props.covariate.type === DrugCovariate.Type.STANDARD && props.covariate.dataType === DrugCovariate.DataType.DOUBLE);
        const fieldIsStandardBool = computed(() => props.covariate.type === DrugCovariate.Type.STANDARD && props.covariate.dataType === DrugCovariate.DataType.BOOL);
        const fieldIsStandardDate = computed(() => props.covariate.type === DrugCovariate.Type.STANDARD && props.covariate.dataType === DrugCovariate.DataType.DATE);
        const fieldIsStandardInt = computed(() => props.covariate.type === DrugCovariate.Type.STANDARD && props.covariate.dataType === DrugCovariate.DataType.INT);


        function handleClose()
        {
            emit("update:isOpen", false);
        }

        function handleOk()
        {
            // Alsow needs to perform a copy of the internal value before returning it,
            // to prevent it from leaking into the parent components.
            emit("save", deepCopy(internalData.value as CovariateValue));

            // Close the modal at the end
            handleClose();
        }

        return {
            requestData,
            internalData,
            handleClose,
            handleOk,

            fieldIsSex,
            filedIsAge,
            fieldIsStandardDouble,
            fieldIsStandardBool,
            fieldIsStandardDate,
            fieldIsStandardInt,
        };
    }
});
